export const COOKIE_CONSENT_NAME = 'll_cookie_consent';
export const COOKIE_CONSENT_ACCEPTED = 'accepted';
export const COOKIE_CONSENT_NECESSARY = 'necessary';
/** 6 months. */
export const COOKIE_CONSENT_MAX_AGE_SECONDS = 60 * 60 * 24 * 180;

export const COOKIE_BANNER_OPEN_EVENT = 'll-open-cookie-banner';

const GTM_ID_PATTERN = /^GTM-[A-Z0-9]+$/i;

const CONSENT_DENIED = {
    analytics_storage: 'denied',
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied'
} as const;

const CONSENT_GRANTED = {
    analytics_storage: 'granted',
    ad_storage: 'granted',
    ad_user_data: 'granted',
    ad_personalization: 'granted'
} as const;

export type DataLayerObject = Record<string, unknown>;

declare global {
    interface Window {
        dataLayer?: DataLayerObject[];
        gtag?: (...args: unknown[]) => void;
    }
}

export function getGtmId(): string {
    const id = process.env.REACT_APP_GTM_ID ?? '';
    return GTM_ID_PATTERN.test(id) ? id : '';
}

/** GTM when a valid container ID is present (prod-only via `.env.prod`). */
export function isGtmEnabled(): boolean {
    return Boolean(getGtmId());
}

function ensureGtag(): NonNullable<Window['gtag']> {
    window.dataLayer = window.dataLayer || [];
    if (!window.gtag) {
        window.gtag = function gtag() {
            // gtag pushes the Arguments object (same as Google's snippet), not a rest array.
            // eslint-disable-next-line prefer-rest-params
            window.dataLayer!.push(arguments as unknown as DataLayerObject);
        };
    }
    return window.gtag;
}

/** Consent default denied, restore accepted from cookie, then load gtm.js. Call before React render. */
export function bootstrapGtm(id: string): void {
    if (typeof window === 'undefined' || typeof document === 'undefined') return;
    if (!GTM_ID_PATTERN.test(id)) return;

    const gtag = ensureGtag();
    gtag('consent', 'default', { ...CONSENT_DENIED, wait_for_update: 500 });
    if (readCookieValue(document.cookie, COOKIE_CONSENT_NAME) === COOKIE_CONSENT_ACCEPTED) {
        updateConsentMode(true);
    }

    window.dataLayer!.push({
        'gtm.start': new Date().getTime(),
        event: 'gtm.js'
    });

    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtm.js?id=${id}`;
    const first = document.getElementsByTagName('script')[0];
    if (first?.parentNode) {
        first.parentNode.insertBefore(script, first);
    } else {
        document.head.appendChild(script);
    }
}

export function trackEvent(event: string, params?: Record<string, string>): void {
    if (typeof window === 'undefined') return;
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event, ...(params ?? {}) });
}

export function readCookieValue(cookieHeader: string, name: string): string | undefined {
    const parts = cookieHeader.split(';');
    for (let i = 0; i < parts.length; i += 1) {
        const trimmed = parts[i].trim();
        const eq = trimmed.indexOf('=');
        if (eq !== -1 && trimmed.slice(0, eq) === name) {
            return trimmed.slice(eq + 1);
        }
    }
    return undefined;
}

export function consentCookieAttributeString(value: string): string {
    const parts = [
        `${COOKIE_CONSENT_NAME}=${value}`,
        'Path=/',
        `Max-Age=${COOKIE_CONSENT_MAX_AGE_SECONDS}`,
        'SameSite=Lax'
    ];
    if (typeof window !== 'undefined' && window.location.protocol === 'https:') {
        parts.push('Secure');
    }
    return parts.join('; ');
}

export function writeConsentCookie(value: string): void {
    if (typeof document === 'undefined') return;
    document.cookie = consentCookieAttributeString(value);
}

export function updateConsentMode(granted: boolean): void {
    if (typeof window === 'undefined') return;
    ensureGtag()('consent', 'update', granted ? CONSENT_GRANTED : CONSENT_DENIED);
}

export function setCookieConsent(accepted: boolean): void {
    writeConsentCookie(accepted ? COOKIE_CONSENT_ACCEPTED : COOKIE_CONSENT_NECESSARY);
    updateConsentMode(accepted);
}

export function openCookieBanner(): void {
    if (typeof window === 'undefined') return;
    window.dispatchEvent(new Event(COOKIE_BANNER_OPEN_EVENT));
}
